import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';
export const ConversionLedgerPage = () => {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const settings = useSettings();
  useEffect(() => { document.title = `Report | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  useEffect(() => {
    const fetchLedgerData = async () => {
      setLoading(true);
      setError(null);
      const token = getCookie('authToken');
      try {
        const baseUrl = process.env.REACT_APP_API_URL || '';

        // Axios GET request with offerId in the path and Authorization header
        const response = await axios.get(`${baseUrl}/api/report/detail/${offerId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Backend response structure parsing (response.data.data contains the array)
        if (response.data && Array.isArray(response.data.data)) {
          setRecords(response.data.data);
        } else if (Array.isArray(response.data)) {
          setRecords(response.data);
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error('Error fetching ledger data:', error);
        setError(
          error.response?.data?.msg || error.response?.data?.message || error.message || 'Something went wrong while fetching data'
        );
        toast.error(error.response?.data?.msg || "An unexpected error occurred.");
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    if (offerId) {
      fetchLedgerData();
    } else {
      navigate('/reports')
    }
  }, [offerId, navigate]);

  // Aggregate Metrics based on backend payload keys
  const totalRevenue = records.reduce((acc, curr) => acc + (curr.user_amount || 0), 0);
  const totalProfit = records.reduce((acc, curr) => acc + (curr.userProfit || 0), 0);

  // Pagination Logic
  const totalRecords = records.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRecords = records.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateValue) => {
    if (!dateValue || dateValue === 'Pending') return 'Pending';
    try {
      const d = new Date(dateValue);
      return isNaN(d.getTime()) ? dateValue : d.toLocaleString();
    } catch {
      return dateValue;
    }
  };

  return (
    <div className="w-full pt-4 sm:pt-20 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans min-h-screen bg-zinc-950 text-zinc-100">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(12px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        .animate-spring-in {
          animation: springIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }

        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }
      `}</style>

      {/* Ambient Background Glows */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

      <div className="max-w-[1600px] mx-auto space-y-8 relative z-10">

        {/* Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-spring-in">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-100">
              Conversion Ledger
            </h1>
            <p className="text-zinc-400 text-xs sm:text-sm font-semibold mt-1 flex items-center gap-2">
              <span>Offer ID:</span>
              <span className="px-2.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg font-mono font-bold text-xs">
                #{offerId}
              </span>
            </p>
          </div>

          <button
            onClick={() => navigate('/reports')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900/90 hover:bg-zinc-800 text-zinc-300 hover:text-zinc-100 rounded-xl text-xs font-bold border border-zinc-800 transition-all micro-interaction shadow-lg shadow-black/40 self-start sm:self-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </button>
        </div>

        {/* Overview Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 animate-spring-in">

          {/* Revenue Card */}
          <div className="bg-zinc-900/80 backdrop-blur-xl p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl flex items-center justify-between overflow-hidden relative group micro-interaction">
            <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-colors" />
            <div className="relative z-10 space-y-1">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
                Estimated Revenue
              </p>
              <h3 className="text-3xl sm:text-4xl font-black tracking-tighter text-zinc-100">
                ₹{totalRevenue.toFixed(2)}
              </h3>
            </div>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-2xl flex items-center justify-center relative z-10 shadow-inner">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 8h6m-6 4h6m-6 4h3m-3-8h3a3 3 0 013 3v0a3 3 0 01-3 3H9V8z" />
              </svg>
            </div>
          </div>

          {/* Profit Card */}
          <div className="bg-zinc-900/80 backdrop-blur-xl p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl flex items-center justify-between overflow-hidden relative group micro-interaction">
            <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-teal-500/10 rounded-full blur-3xl group-hover:bg-teal-500/20 transition-colors" />
            <div className="relative z-10 space-y-1">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
                Your Net Profit
              </p>
              <h3 className="text-3xl sm:text-4xl font-black tracking-tighter text-emerald-400">
                ₹{totalProfit.toFixed(2)}
              </h3>
            </div>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-teal-500/10 text-teal-400 border border-teal-500/20 rounded-2xl flex items-center justify-center relative z-10 shadow-inner">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>

        </div>

        {/* Conversion Ledger Table Section */}
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] shadow-2xl overflow-hidden animate-spring-in">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-zinc-950/60 border-b border-zinc-800/80">
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400">User Payout Info</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400">Profit Metrics</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400">Network Details</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400">Device/OS</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400">Status</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-zinc-400 text-right">Timestamps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-zinc-400 text-sm font-semibold">
                      Loading conversion ledger...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-rose-400 text-sm font-semibold">
                      {error}
                    </td>
                  </tr>
                ) : paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-zinc-500 text-sm font-semibold">
                      No records found for this offer.
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((item, index) => (
                    <tr key={item.tid || index} className="hover:bg-zinc-800/30 transition-colors">

                      {/* User Info (user_upi, refer_upi) */}
                      <td className="px-6 py-5">
                        <p className="font-bold text-zinc-200 text-sm">{item.user_upi || 'N/A'}</p>
                        <p className="text-[10px] font-bold text-zinc-500 mt-1 uppercase tracking-tighter">
                          Ref: {item.refer_upi || 'N/A'}
                        </p>
                      </td>

                      {/* Profit Metrics (user_amount, userProfit) */}
                      <td className="px-6 py-5 text-sm">
                        <div className="flex flex-col">
                          <span className="font-black text-emerald-400">₹{item.user_amount}</span>
                          <span className="text-[10px] font-bold text-teal-400 uppercase tracking-tight">
                            Profit: ₹{item.userProfit}
                          </span>
                        </div>
                      </td>

                      {/* Network Details (click_ip, location, carrier) */}
                      <td className="px-6 py-5">
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-zinc-300 flex items-center gap-1.5 font-mono">
                            <svg className="w-3.5 h-3.5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                            </svg>
                            {item.click_ip || 'N/A'}
                          </p>
                          <p className="text-[10px] font-medium text-zinc-500">
                            {item.location} • {item.carrier}
                          </p>
                        </div>
                      </td>

                      {/* Device / OS (device, os, browser) */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-zinc-950 border border-zinc-800 rounded-xl flex items-center justify-center text-zinc-400 shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-[11px] font-bold text-zinc-200 leading-tight">{item.device}</p>
                            <p className="text-[10px] font-medium text-zinc-500">{item.os} / {item.browser}</p>
                          </div>
                        </div>
                      </td>

                      {/* Status (isLead, validation) */}
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1.5 w-24">
                          <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase text-center border ${item.isLead
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                              : 'bg-zinc-950 text-zinc-400 border-zinc-800/80'
                            }`}>
                            {item.isLead ? 'Lead' : 'Click Only'}
                          </span>
                          <span className="px-2 py-1 rounded-lg text-[9px] font-black uppercase text-center bg-zinc-900 text-zinc-300 border border-zinc-800">
                            {item.validation}
                          </span>
                        </div>
                      </td>

                      {/* Timestamps (convTime, clickTime) */}
                      <td className="px-6 py-5 text-right">
                        <p className="text-[10px] font-black text-zinc-200">
                          Conv: {formatDate(item.convTime)}
                        </p>
                        <p className="text-[9px] font-mono text-zinc-500 mt-1 uppercase tracking-tighter">
                          Click: {formatDate(item.clickTime)}
                        </p>
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer / Pagination */}
          <div className="p-6 bg-zinc-950/60 border-t border-zinc-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
              Showing {paginatedRecords.length} of {totalRecords} records
            </p>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 disabled:opacity-30 disabled:hover:text-zinc-400 transition-all micro-interaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center px-4 py-1.5 bg-zinc-900 border border-zinc-800 rounded-xl text-xs font-black text-zinc-200 font-mono">
                {currentPage} / {totalPages}
              </div>

              <button
                disabled={currentPage === totalPages || loading}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 disabled:opacity-30 disabled:hover:text-zinc-400 transition-all micro-interaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default ConversionLedgerPage;